/**
 * KwhInput — input numerico per le letture del contatore.
 *
 * Replica del componente mobile `KwhInput`:
 *  - accetta solo cifre + virgola (rimpiazza il punto)
 *  - massimo 3 decimali dopo la virgola
 *  - max 7 cifre intere (limite ragionevole per contatore residenziale)
 *  - suffisso "kWh" visibile dentro l'input
 *  - error inline con bordo rosso
 *  - forwardRef per `.focus()` programmatico (passaggio tra fasce)
 */

import { forwardRef, type InputHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'type'> & {
  label: string
  value: string
  onChange: (sanitized: string) => void
  error?: string | null
}

function sanitize(raw: string): string {
  // Punto → virgola
  let v = raw.replace(/\./g, ',')
  // Tieni solo cifre e virgola
  v = v.replace(/[^\d,]/g, '')
  // Una sola virgola: tieni la prima, scarta le altre
  const firstCommaIdx = v.indexOf(',')
  if (firstCommaIdx !== -1) {
    const intPart = v.slice(0, firstCommaIdx).slice(0, 7)
    const decPart = v.slice(firstCommaIdx + 1).replace(/,/g, '').slice(0, 3)
    return `${intPart},${decPart}`
  }
  // No virgola: max 7 cifre intere
  return v.slice(0, 7)
}

export const KwhInput = forwardRef<HTMLInputElement, Props>(function KwhInput(
  { label, value, onChange, error, placeholder = 'Es. 1234,56', id, ...rest },
  ref,
) {
  const inputId = id ?? `kwh-${label.replace(/\s+/g, '-').toLowerCase()}`
  const errorId = `${inputId}-error`
  const hasError = Boolean(error)

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className="text-sm font-semibold text-foreground">
        {label}
      </label>
      <div
        className={cn(
          'flex h-12 items-center rounded-xl border-[1.5px] bg-background px-4 text-[15px] transition-colors',
          'focus-within:outline-none',
          hasError
            ? 'border-destructive focus-within:border-destructive'
            : 'border-input focus-within:border-secondary',
        )}
      >
        <input
          id={inputId}
          ref={ref}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(sanitize(e.target.value))}
          aria-invalid={hasError}
          aria-describedby={hasError ? errorId : undefined}
          className="flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
          {...rest}
        />
        <span className="ml-2 text-sm font-medium text-muted-foreground">kWh</span>
      </div>
      {hasError && (
        <p id={errorId} className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  )
})
